import type { PlanDefinition } from '@opinix-cloud/shared';
import type { BillingCycle } from '../pages/PricingPage';

interface PlanCardProps {
  plan: PlanDefinition;
  cycle: BillingCycle;
  /** True if this is the recommended/highlighted plan (PRO by default). */
  featured?: boolean;
  /** Click handler for the primary CTA. */
  onCta: () => void;
}

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function PlanCard({
  plan,
  cycle,
  featured = false,
  onCta,
}: PlanCardProps): JSX.Element {
  const { ctaLabel, helperText } = ctaCopy(plan);

  return (
    <div
      className={`relative flex flex-col rounded-xl border p-6 transition-all duration-200 ${
        featured
          ? 'border-cyan-500/60 bg-white shadow-lg shadow-cyan-500/10 ring-1 ring-cyan-500/20'
          : 'border-steel-200 bg-white shadow-sm hover:shadow-md hover:border-steel-300'
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-cyan-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
          Most popular
        </div>
      )}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-steel-500">
          {plan.name}
        </h3>
        <PriceDisplay plan={plan} cycle={cycle} />
        <p className="mt-3 text-sm text-steel-600 leading-relaxed min-h-[2.5rem]">
          {plan.tagline}
        </p>
      </div>

      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.features.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-2 text-sm text-steel-700"
          >
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <button
          type="button"
          onClick={onCta}
          className={`btn w-full ${
            featured ? 'btn-primary' : 'btn-outline-dark'
          }`}
        >
          {ctaLabel}
        </button>
        {helperText && (
          <p className="mt-2 text-xs text-center text-steel-500">
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
}

function PriceDisplay({
  plan,
  cycle,
}: {
  plan: PlanDefinition;
  cycle: BillingCycle;
}): JSX.Element {
  // Enterprise — sales-led, no public price.
  if (plan.pricePerMonth === null && plan.pricePerYear === null) {
    return (
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-extrabold tracking-tightest text-navy-900">
          Custom
        </span>
      </div>
    );
  }

  // Annual cycle: show the per-month equivalent of the annual price so the
  // "save 2 months" math is visible without forcing the visitor to do it.
  const monthly = plan.pricePerMonth ?? 0;
  const annual = plan.pricePerYear ?? monthly * 12;
  const displayed = cycle === 'annual' ? Math.round(annual / 12) : monthly;

  return (
    <div className="mt-3">
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-extrabold tracking-tightest text-navy-900">
          {USD.format(displayed)}
        </span>
        <span className="text-sm font-medium text-steel-500">/ month</span>
      </div>
      {cycle === 'annual' && (
        <p className="mt-1 text-xs text-steel-500">
          Billed {USD.format(annual)} / year
        </p>
      )}
    </div>
  );
}

function ctaCopy(plan: PlanDefinition): {
  ctaLabel: string;
  helperText: string | null;
} {
  switch (plan.tier) {
    case 'STARTER':
      return {
        ctaLabel: 'Get started',
        helperText: 'Early access list',
      };
    case 'PRO':
      return {
        ctaLabel: 'Get started',
        helperText: 'Early access list',
      };
    case 'ENTERPRISE':
    case 'UNLIMITED':
      return {
        ctaLabel: 'Contact sales',
        helperText: 'Custom pricing & SLAs',
      };
  }
}

function CheckIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      className="mt-0.5 h-4 w-4 flex-none text-cyan-500"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5a1 1 0 1 1 1.4-1.4l3.9 3.9 6.7-6.7a1 1 0 0 1 1.4 0Z"
      />
    </svg>
  );
}
